import jsPDF from 'jspdf';

export const generatePredictionPDF = (userData) => {
  const { 
    user, 
    hormones, 
    lifestyle, 
    prediction, 
    wellnessScore,
    estimatedMenopause 
  } = userData;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 20;

  // Helper function to add text
  const addText = (text, x, y, options = {}) => {
    doc.setFontSize(options.size || 12);
    doc.setFont('helvetica', options.style || 'normal');
    doc.setTextColor(options.color || '#1E1B4B');
    doc.text(text, x, y);
    return y + (options.lineHeight || 7);
  };

  // Helper for horizontal line
  const addLine = (y) => {
    doc.setDrawColor(200, 200, 200);
    doc.line(20, y, pageWidth - 20, y);
    return y + 5;
  };

  // Header
  doc.setFillColor(91, 33, 182);
  doc.rect(0, 0, pageWidth, 35, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('MenoPath', 20, 20);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Clinical Assessment Report', 20, 28);
  
  doc.setFontSize(10);
  doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })}`, pageWidth - 70, 28);

  yPos = 50;

  // Patient Information
  yPos = addText('Patient Information', 20, yPos, { size: 14, style: 'bold', color: '#5B21B6' });
  yPos = addLine(yPos);
  
  yPos = addText(`Name: ${user?.name || 'N/A'}`, 20, yPos);
  yPos = addText(`Age: ${lifestyle?.age || 'N/A'} years`, 20, yPos);
  if (lifestyle?.ethnicity) {
    yPos = addText(`Ethnicity: ${lifestyle.ethnicity.replace(/_/g, ' ')}`, 20, yPos);
  }
  yPos = addText(`Report Date: ${new Date().toLocaleDateString()}`, 20, yPos);
  
  yPos += 10;

  // Prediction Summary
  yPos = addText('Menopause Prediction Summary', 20, yPos, { size: 14, style: 'bold', color: '#5B21B6' });
  yPos = addLine(yPos);

  if (estimatedMenopause) {
    doc.setFillColor(252, 231, 243);
    doc.roundedRect(20, yPos, pageWidth - 40, 25, 3, 3, 'F');
    
    doc.setTextColor(157, 23, 77);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Predicted Menopause Onset: Age ${estimatedMenopause.min} - ${estimatedMenopause.max}`, 30, yPos + 10);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Confidence: ${prediction?.confidence || 'Medium'}`, 30, yPos + 18);
    doc.text(`Stage: ${prediction?.stage?.replace(/_/g, ' ') || 'N/A'}`, 100, yPos + 18);
    
    yPos += 35;
  }

  // Wellness Score
  yPos = addText(`Wellness Score: ${wellnessScore}/100`, 20, yPos, { size: 12, style: 'bold' });
  yPos += 5;

  // Hormonal Data
  yPos = addText('Hormonal Biomarkers', 20, yPos, { size: 14, style: 'bold', color: '#5B21B6' });
  yPos = addLine(yPos);

  const hormoneLabels = {
    fsh: { name: 'FSH (Follicle Stimulating Hormone)', unit: 'mIU/mL', range: '3.5-12.5' },
    lh: { name: 'LH (Luteinizing Hormone)', unit: 'mIU/mL', range: '2.4-12.6' },
    estradiol_e2: { name: 'Estradiol (E2)', unit: 'pg/mL', range: '30-400' },
    amh: { name: 'AMH (Anti-Müllerian Hormone)', unit: 'ng/mL', range: '1.0-3.5' },
    inhibin_b: { name: 'Inhibin B', unit: 'pg/mL', range: '20-150' },
    progesterone: { name: 'Progesterone', unit: 'ng/mL', range: '0.2-25' }
  };

  if (hormones) {
    Object.entries(hormoneLabels).forEach(([key, info]) => {
      const value = hormones[key];
      if (value !== undefined && value !== null) {
        yPos = addText(`${info.name}:`, 25, yPos, { size: 10 });
        yPos = addText(`  Value: ${value} ${info.unit}  |  Normal Range: ${info.range}`, 25, yPos, { size: 9, color: '#64748B' });
        yPos += 2;
      }
    });
  } else {
    yPos = addText('No hormonal data available', 25, yPos, { size: 10, color: '#64748B' });
  }

  yPos += 8;

  // Lifestyle Factors
  yPos = addText('Lifestyle Factors', 20, yPos, { size: 14, style: 'bold', color: '#5B21B6' });
  yPos = addLine(yPos);

  if (lifestyle) {
    const lifestyleItems = [
      { label: 'Smoking Status', value: lifestyle.smoking_status },
      { label: 'Alcohol Consumption', value: lifestyle.alcohol_consumption },
      { label: 'Exercise Frequency', value: lifestyle.exercise_frequency },
      { label: 'Sleep Quality', value: `${lifestyle.sleep_quality}/10` },
      { label: 'Sleep Hours', value: `${lifestyle.sleep_hours} hours` },
      { label: 'BMI', value: lifestyle.bmi }
    ];

    lifestyleItems.forEach(item => {
      if (item.value) {
        yPos = addText(`${item.label}: ${String(item.value).replace(/_/g, ' ')}`, 25, yPos, { size: 10 });
      }
    });
  } else {
    yPos = addText('No lifestyle data available', 25, yPos, { size: 10, color: '#64748B' });
  }

  yPos += 8;

  // AI Insights
  if (prediction?.insights) {
    yPos = addText('Clinical Insights', 20, yPos, { size: 14, style: 'bold', color: '#5B21B6' });
    yPos = addLine(yPos);
    
    // Split insights into lines that fit
    const maxWidth = pageWidth - 50;
    const lines = doc.splitTextToSize(prediction.insights, maxWidth);
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    lines.forEach(line => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      doc.text(line, 25, yPos);
      yPos += 5;
    });
    
    yPos += 5;
  }

  // Recommendations
  if (prediction?.recommendations && prediction.recommendations.length > 0) {
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }
    
    yPos = addText('Recommendations', 20, yPos, { size: 14, style: 'bold', color: '#5B21B6' });
    yPos = addLine(yPos);
    
    prediction.recommendations.forEach((rec, index) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      yPos = addText(`${index + 1}. ${rec}`, 25, yPos, { size: 10 });
    });
  }

  // Footer / Disclaimer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(
      'DISCLAIMER: This report is for informational purposes only and should not replace professional medical advice.',
      pageWidth / 2,
      285,
      { align: 'center' }
    );
    doc.text(
      `Page ${i} of ${pageCount} | MenoPath.ai`,
      pageWidth / 2,
      290,
      { align: 'center' }
    );
  }

  return doc;
};

export const downloadPredictionPDF = (userData) => {
  const doc = generatePredictionPDF(userData);
  const fileName = `MenoPath_Report_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};
